'use client';

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useAuth } from '@/lib/integrations/supabase';
import { needsOnboarding } from '@/lib/features/onboarding';

// Lazy load OnboardingWizard - it's rarely shown and has many dependencies
const OnboardingWizard = dynamic(
  () => import('./onboarding-wizard').then(mod => ({ default: mod.OnboardingWizard })),
  { ssr: false }
);

export function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const { businessId, role, isLoading } = useAuth();
  const [shouldShowOnboarding, setShouldShowOnboarding] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkOnboarding = async () => {
      // Skip onboarding entirely for admin users
      if (role === 'admin') {
        setShouldShowOnboarding(false);
        setIsChecking(false);
        return;
      }

      // Check for debug flag first - don't remove it, keep it until wizard closes
      const forceOnboarding = typeof window !== 'undefined' && sessionStorage.getItem('force_onboarding') === 'true';
      if (forceOnboarding && businessId) {
        setShouldShowOnboarding(true);
        setIsChecking(false);
        return;
      }

      // Only check for owners
      if (isLoading || role !== 'owner' || !businessId) {
        setIsChecking(false);
        setShouldShowOnboarding(false);
        return;
      }

      try {
        const needs = await needsOnboarding(businessId);
        setShouldShowOnboarding(needs);
      } catch {
        setShouldShowOnboarding(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkOnboarding();
  }, [businessId, role, isLoading]);

  if (isChecking || isLoading) {
    return <>{children}</>;
  }

  if (shouldShowOnboarding && businessId) {
    return (
      <OnboardingWizard
        businessId={businessId}
        onClose={() => {
          setShouldShowOnboarding(false);
        }}
      />
    );
  }

  return <>{children}</>;
}

